import { useParallax, useScrollPosition } from '../hooks/useParallax';
import mg1 from "../assets/1736617763762__1_-removebg-preview.png"
import { Link } from 'react-router-dom';
import CVDownload from './CVDownload';

const Hero = () => {
    const parallaxOffset = useParallax(0.3);
    const scrollPosition = useScrollPosition();
    const imageParallax = useParallax(0.2);
    const backgroundParallax = useParallax(0.1);

    return (
        <section id="hero" className="pt-20  sm:pt-24 md:pt-32 lg:pt-40 max-w-7xl mx-auto pb-12 sm:pb-16 md:pb-20 lg:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
            {/* Parallax Background Elements */}
            <div
                className="absolute inset-0 opacity-20 sm:opacity-30"
                style={{ transform: `translateY(${backgroundParallax}px)` }}
            >
                <div className="absolute top-10 sm:top-20 left-4 sm:left-10 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-primary/10 rounded-full blur-2xl sm:blur-3xl"></div>
                <div className="absolute bottom-10 sm:bottom-20 right-4 sm:right-10 w-48 h-48 sm:w-72 sm:h-72 md:w-96 md:h-96 bg-purple-500/10 rounded-full blur-2xl sm:blur-3xl"></div>
            </div>

            <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center relative z-10">
                <div
                    className="space-y-4 sm:space-y-5 md:space-y-6 animate-fade-in text-center lg:text-left"
                    style={{ transform: `translateY(${parallaxOffset}px)` }}
                >
                    <div className="inline-flex items-center  gap-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark px-3 py-1.5 rounded-full text-xs font-medium">
                        <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
                        Available for new projects
                    </div>

                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-gray-900 dark:text-white leading-[1.1]">
                        Hi, I'm <br />
                        <span
                            className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-green-400 to-purple-500 animate-gradient"
                            style={{
                                transform: `translateX(${scrollPosition * 0.1}px)`,
                                transition: 'transform 0.1s ease-out'
                            }}
                        >
                            Md.Azaharul.
                        </span>
                    </h1>

                    <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-lg leading-relaxed mx-auto lg:mx-0">
                        A <strong className="text-gray-900 dark:text-white">Mernstack Web Developer</strong> based in Bangladesh.
                        I craft accessible, pixel-perfect, and performant web experiences using modern technologies.
                    </p>

                    {/* Responsive Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-4 sm:pt-6">
                        <Link to="/contact"
                            className="w-full sm:w-auto bg-gray-900 dark:bg-white text-white dark:text-black px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 rounded-full font-medium sm:font-semibold flex items-center justify-center gap-2 hover:opacity-90 hover:scale-105 transition-all duration-300 text-sm sm:text-base touch-manipulation"
                        >
                            Contact Me
                            <span className="material-icons-round text-sm transform -rotate-45 animate-bounce-x">
                                arrow_forward
                            </span>
                        </Link>
                        <CVDownload
                            className="w-full sm:w-auto px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 rounded-full font-medium flex items-center justify-center gap-2 hover:text-primary hover:scale-105 transition-all duration-300 text-sm sm:text-base border border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Download CV
                            <span className="material-icons-round text-sm animate-bounce-y">
                                download
                            </span>
                        </CVDownload>
                    </div>
                </div>

                <div
                    className="relative flex justify-center animate-fade-in-up mt-8 lg:mt-0"
                    style={{ transform: `translateY(${imageParallax}px)` }}
                >
                    <div className="relative group w-full max-w-xs sm:max-w-sm md:max-w-md">
                        <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-500 rounded-2xl sm:rounded-3xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div
                            className="relative w-full aspect-square rounded-2xl sm:rounded-3xl p-1 bg-gradient-to-tr from-green-500/50 via-purple-500/30 to-gray-800/50 dark:from-green-500/30 dark:via-purple-500/10 dark:to-gray-800/30 hover:scale-105 transition-transform duration-500 shadow-xl sm:shadow-2xl"
                            style={{
                                transform: `translateY(${imageParallax}px) rotateY(${scrollPosition * 0.02}deg)`,
                                transformStyle: 'preserve-3d'
                            }}
                        >
                            <div className="w-full h-full bg-surface-light dark:bg-surface-dark rounded-[1rem] sm:rounded-[1.3rem] overflow-hidden relative">
                                <div
                                    className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 bg-purple-500/20 blur-2xl sm:blur-3xl rounded-full animate-float"
                                    style={{
                                        transform: `translateX(${scrollPosition * 0.05}px)`
                                    }}
                                ></div>
                                <div
                                    className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 bg-primary/20 blur-2xl sm:blur-3xl rounded-full animate-float-delayed"
                                    style={{
                                        transform: `translateX(${-scrollPosition * 0.03}px)`
                                    }}
                                ></div>
                                <img
                                    alt="Portrait of Azaharul"
                                    className="w-full h-full object-cover object-top opacity-90 hover:scale-105 transition-transform duration-700"
                                    src={mg1}
                                    style={{
                                        transform: `scale(${1 + scrollPosition * 0.0001})`,
                                        transition: 'transform 0.1s ease-out'
                                    }}
                                />
                            </div>
                            <div
                                className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 md:-top-4 md:-right-4 bg-surface-dark border border-border-dark p-2 sm:p-3 rounded-full shadow-lg hidden sm:block animate-spin-slow"
                                style={{
                                    transform: `rotate(${scrollPosition * 0.5}deg) scale(${1 + Math.sin(scrollPosition * 0.01) * 0.1})`
                                }}
                            >
                                <span className="material-icons-round text-primary text-sm sm:text-base">code</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Parallax Floating Elements - Hidden on mobile for performance */}
                <div
                    className="absolute top-1/4 left-1/4 w-3 h-3 sm:w-4 sm:h-4 bg-primary/30 rounded-full hidden sm:block"
                    style={{
                        transform: `translateY(${scrollPosition * 0.3}px) translateX(${Math.sin(scrollPosition * 0.01) * 20}px)`,
                        opacity: 0.6
                    }}
                ></div>
                <div
                    className="absolute top-3/4 right-1/4 w-4 h-4 sm:w-6 sm:h-6 bg-purple-500/30 rounded-full hidden sm:block"
                    style={{
                        transform: `translateY(${-scrollPosition * 0.2}px) translateX(${Math.cos(scrollPosition * 0.01) * 15}px)`,
                        opacity: 0.4
                    }}
                ></div>
            </div>
        </section>
    );
};

export default Hero;